import { RemovableCard, Text } from '@dixa/lib-shared-habla';
import type { Tag } from 'lib-shared-types-api';
import { useCallback, useEffect, useState } from 'react';

import { TagSelect } from 'components/tagSelect';

import {
  BulkModalActionBaseProps,
  BulkModalActionType,
  BulkModalTagAction,
} from '../../../types/conversationsBulkModal.types';
import { conversationsBulkModalActionLabel } from '../../../utils/conversationsBulkModalActionLabel';

type ConversationsBulkModalTagActionProps = BulkModalActionBaseProps<
  BulkModalTagAction,
  {
    /** The type of tag action. */
    type: Extract<BulkModalActionType, 'add_tag' | 'remove_tag'>;
  }
>;

/**
 * Represents the component for the conversations bulk modal add or remove tag
 * action.
 */
export const ConversationsBulkModalTagAction = ({
  conversationIds,
  type,
  onChange,
  onRemove,
}: ConversationsBulkModalTagActionProps): JSX.Element => {
  const [selectedTags, setSelectedTags] = useState<readonly Tag[]>([]);

  useEffect(() => {
    onChange({
      type,
      isValid: selectedTags.length > 0,
      arg: {
        action: type,
        csids: conversationIds,
        names: selectedTags.map(({ name }) => name),
      },
    });
  }, [onChange, selectedTags, type, conversationIds]);

  /** Handles the change of selected tags. */
  const handleChange = useCallback((tags: readonly Tag[]) => {
    setSelectedTags(tags);
  }, []);

  return (
    <RemovableCard
      icon="tag"
      title={conversationsBulkModalActionLabel[type]}
      onRemove={() => onRemove(type)}
    >
      <Text.Paragraph subdued withMarginBottom>
        {type === 'add_tag'
          ? 'Select the tags you wish to bulk add to the selected conversations.'
          : 'Select the tags you wish to bulk remove from the selected conversations.'}
      </Text.Paragraph>

      <TagSelect
        selectedTags={selectedTags as Tag[]}
        allowMultiple
        allowTagCreation={type === 'add_tag'}
        includeDeactivated={type === 'remove_tag'}
        allowSelectDeactivated={type === 'remove_tag'}
        onChange={handleChange}
        placeholder={
          type === 'add_tag'
            ? 'Select one or more tags to add'
            : 'Select one or more tags to remove'
        }
      />
    </RemovableCard>
  );
};
